import axios from 'axios';
import { Globals } from '@/modules/Globals';
import type {
  IAnswerQuestionDto,
  IApplyProposalDto,
  ICopyMenuImportDto,
  ICreateMenuImportDto,
  IDraftDeltaResult,
  IDraftPatchDto,
  IDraftPatchResult,
  ILiveBody,
  IMenuImportPage,
  IMenuImportPageFields,
  IMenuImportRead,
  IMenuImportSnapshot,
  IMenuImportTextDto,
  IMenuImportTurn,
  IPublishDto,
  IPublishResponse,
  ISendTurnDto,
  IStartReadDto,
  IUndoDto,
  IUndoUpdateResponse,
  TDraftOp,
} from '@/interfaces/IMenuImport';
import type { ICopySource } from '@/interfaces/IStoreSetup';

const apiUrl = Globals.apiPath + '/menu-imports';

// Every call has a timeout: a request on a connection that died silently
// (weak signal, captive portal) would otherwise stay pending for minutes and
// hold the serial save queue behind it. A timeout rejects without an answer,
// which the callers retry like any other network error.

/** Small writes, presence and the copy sources. */
export const MENU_IMPORT_WRITE_TIMEOUT_MS = 20000;
/** Snapshots carry the whole draft (up to about 2 MB). */
export const MENU_IMPORT_SNAPSHOT_TIMEOUT_MS = 30000;
/** Publish and copy write a whole catalog in one transaction. */
export const MENU_IMPORT_PUBLISH_TIMEOUT_MS = 60000;
/** A page upload may carry a 5 MB photo over a slow phone connection. */
export const PAGE_UPLOAD_TIMEOUT_MS = 60000;

const WRITE = { timeout: MENU_IMPORT_WRITE_TIMEOUT_MS };
const SNAPSHOT = { timeout: MENU_IMPORT_SNAPSHOT_TIMEOUT_MS };
const LONG_WRITE = { timeout: MENU_IMPORT_PUBLISH_TIMEOUT_MS };

/** `/menu-imports/{sid}` plus the given path segments, each URL-encoded. */
function sessionUrl(sessionId: string, ...segments: string[]): string {
  const path = [sessionId, ...segments].map(encodeURIComponent).join('/');
  return `${apiUrl}/${path}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Fraction 0..1 of an axios upload progress event; null when the size is unknown. */
export function uploadFraction(event: unknown): number | null {
  if (!isRecord(event)) return null;
  const { loaded, total } = event;
  if (typeof loaded !== 'number' || typeof total !== 'number' || total <= 0)
    return null;
  return Math.min(1, Math.max(0, loaded / total));
}

/** The multipart body of a page upload: the fields first, then the JPEG. */
export function pageFormData(
  blob: Blob,
  fields: IMenuImportPageFields
): FormData {
  const form = new FormData();
  form.append('clientPageId', fields.clientPageId);
  form.append('origin', fields.origin);
  if (fields.originName) form.append('originName', fields.originName);
  if (fields.originPage !== null && fields.originPage !== undefined)
    form.append('originPage', String(fields.originPage));
  if (fields.originPart !== null && fields.originPart !== undefined)
    form.append('originPart', String(fields.originPart));
  if (fields.clientWarning) form.append('clientWarning', fields.clientWarning);
  form.append('file', blob, `${fields.clientPageId}.jpg`);
  return form;
}

/** `/menu-imports/*`: the AI menu import sessions (plan §8.3). */
export class MenuImportsService {
  /** The store's active session; null when it has none (204). */
  static async active(
    restaurantId: number
  ): Promise<IMenuImportSnapshot | null> {
    const res = await axios.get<IMenuImportSnapshot | ''>(
      `${apiUrl}/active?restaurantId=${restaurantId}`,
      SNAPSHOT
    );
    if (res.status === 204 || !res.data) return null;
    return res.data;
  }

  /** A new session (201), or the store's active one (200). */
  static async create(
    body: ICreateMenuImportDto
  ): Promise<IMenuImportSnapshot> {
    const res = await axios.post<IMenuImportSnapshot>(apiUrl, body, SNAPSHOT);
    return res.data;
  }

  /** Other own stores with dishes to copy from (at most 3). */
  static async copySources(restaurantId: number): Promise<ICopySource[]> {
    const res = await axios.get<ICopySource[]>(
      `${apiUrl}/copy-sources?restaurantId=${restaurantId}`,
      WRITE
    );
    return res.data;
  }

  static async copy(body: ICopyMenuImportDto): Promise<IMenuImportSnapshot> {
    const res = await axios.post<IMenuImportSnapshot>(
      `${apiUrl}/copy`,
      body,
      LONG_WRITE
    );
    return res.data;
  }

  /** `draft` is null in the answer when `sinceRev` equals the current rev. */
  static async snapshot(
    sessionId: string,
    sinceRev?: number
  ): Promise<IMenuImportSnapshot> {
    const query = sinceRev === undefined ? '' : `?sinceRev=${sinceRev}`;
    const res = await axios.get<IMenuImportSnapshot>(
      sessionUrl(sessionId) + query,
      SNAPSHOT
    );
    return res.data;
  }

  /** 204. */
  static async presence(sessionId: string): Promise<void> {
    await axios.post(sessionUrl(sessionId, 'presence'), undefined, WRITE);
  }

  /** One processed JPEG page (a replay with the same `clientPageId` returns the stored page). */
  static async uploadPage(
    sessionId: string,
    blob: Blob,
    fields: IMenuImportPageFields,
    onProgress?: (fraction: number) => void
  ): Promise<IMenuImportPage> {
    const res = await axios.post<IMenuImportPage>(
      sessionUrl(sessionId, 'pages'),
      pageFormData(blob, fields),
      {
        timeout: PAGE_UPLOAD_TIMEOUT_MS,
        onUploadProgress: (event: unknown) => {
          const fraction = uploadFraction(event);
          if (fraction !== null) onProgress?.(fraction);
        },
      }
    );
    return res.data;
  }

  /** A replay with the same `clientPageId` returns the stored page. */
  static async addText(
    sessionId: string,
    body: IMenuImportTextDto
  ): Promise<IMenuImportPage> {
    const res = await axios.post<IMenuImportPage>(
      sessionUrl(sessionId, 'text'),
      body,
      WRITE
    );
    return res.data;
  }

  /** 204; only unread pages can be removed (409 `PAGE_ALREADY_READ`). */
  static async deletePage(sessionId: string, pageId: string): Promise<void> {
    await axios.delete(sessionUrl(sessionId, 'pages', pageId), WRITE);
  }

  /** 202. */
  static async startRead(
    sessionId: string,
    body: IStartReadDto
  ): Promise<IMenuImportRead> {
    const res = await axios.post<IMenuImportRead>(
      sessionUrl(sessionId, 'reads'),
      body,
      WRITE
    );
    return res.data;
  }

  /** 202. */
  static async cancelRead(sessionId: string): Promise<void> {
    await axios.post(
      sessionUrl(sessionId, 'reads', 'cancel'),
      undefined,
      WRITE
    );
  }

  /** `clientMutationId` makes a retry of the same mutation idempotent (answered `replayed`). */
  static async patchDraft(
    sessionId: string,
    ops: TDraftOp[],
    clientMutationId?: string
  ): Promise<IDraftPatchResult> {
    const body: IDraftPatchDto = clientMutationId
      ? { ops, clientMutationId }
      : { ops };
    const res = await axios.patch<IDraftPatchResult>(
      sessionUrl(sessionId, 'draft'),
      body,
      WRITE
    );
    return res.data;
  }

  /** `clientMutationId` makes a retry of the same answer idempotent (answered `replayed`). */
  static async answer(
    sessionId: string,
    questionId: string,
    body: IAnswerQuestionDto,
    clientMutationId?: string
  ): Promise<IDraftDeltaResult> {
    const dto: IAnswerQuestionDto = clientMutationId
      ? { ...body, clientMutationId }
      : body;
    const res = await axios.post<IDraftDeltaResult>(
      sessionUrl(sessionId, 'questions', questionId, 'answer'),
      dto,
      WRITE
    );
    return res.data;
  }

  /** 202: the turn is `thinking`; its result arrives by socket (or polling). */
  static async sendTurn(
    sessionId: string,
    body: ISendTurnDto
  ): Promise<IMenuImportTurn> {
    const res = await axios.post<IMenuImportTurn>(
      sessionUrl(sessionId, 'turns'),
      body,
      WRITE
    );
    return res.data;
  }

  /** `clientMutationId` makes a retry of the same apply idempotent (answered `replayed`). */
  static async applyProposal(
    sessionId: string,
    turnId: string,
    proposalId: string,
    clientMutationId?: string
  ): Promise<IDraftDeltaResult> {
    const dto: IApplyProposalDto | undefined = clientMutationId
      ? { clientMutationId }
      : undefined;
    const res = await axios.post<IDraftDeltaResult>(
      sessionUrl(sessionId, 'turns', turnId, 'proposals', proposalId, 'apply'),
      dto,
      WRITE
    );
    return res.data;
  }

  /** 204. */
  static async discardProposal(
    sessionId: string,
    turnId: string,
    proposalId: string
  ): Promise<void> {
    await axios.post(
      sessionUrl(
        sessionId,
        'turns',
        turnId,
        'proposals',
        proposalId,
        'discard'
      ),
      undefined,
      WRITE
    );
  }

  /** `clientMutationId` makes a retry of the same undo idempotent (answered `replayed`). */
  static async undo(
    sessionId: string,
    rev: number,
    clientMutationId?: string
  ): Promise<IDraftDeltaResult> {
    const dto: IUndoDto = clientMutationId
      ? { rev, clientMutationId }
      : { rev };
    const res = await axios.post<IDraftDeltaResult>(
      sessionUrl(sessionId, 'undo'),
      dto,
      WRITE
    );
    return res.data;
  }

  /** A published session answers its stored result (`replayed`). */
  static async publish(
    sessionId: string,
    body: IPublishDto
  ): Promise<IPublishResponse> {
    const res = await axios.post<IPublishResponse>(
      sessionUrl(sessionId, 'publish'),
      body,
      LONG_WRITE
    );
    return res.data;
  }

  /**
   * Update sessions: re-reads the store's menu and compares the draft again,
   * after one owner decision when `body` has one (idempotent with its
   * `clientMutationId`).
   */
  static async live(
    sessionId: string,
    body: ILiveBody
  ): Promise<IDraftDeltaResult> {
    const res = await axios.post<IDraftDeltaResult>(
      sessionUrl(sessionId, 'live'),
      body,
      WRITE
    );
    return res.data;
  }

  /** «Deshacer» of a published update (idempotent: a retry answers the same). */
  static async undoUpdate(sessionId: string): Promise<IUndoUpdateResponse> {
    const res = await axios.post<IUndoUpdateResponse>(
      sessionUrl(sessionId, 'undo-update'),
      {},
      WRITE
    );
    return res.data;
  }
}
